import { SortOrderEnum } from "modules/collections/details/model/collection";
import { EmbeddedCardPrice } from "modules/cards/model/card-price";
import {
  type GameId,
  type PricingConfiguration,
} from "../../common/model/models";

export enum SetSortOptionsEnum {
  Name = "name",
  Code = "code",
  ReleaseDate = "releaseDate",
  BaseSetSize = "baseSetSize",
  TotalSetSize = "totalSetSize",
  MarketCap = "marketCap",
}

export interface SetPagedRequest {
  page: number;
  perPage: number;
  query: string;
  sort: SetSortOptionsEnum;
  order: SortOrderEnum;
  game: GameId;
}

export interface SetPagedResponse {
  sets: CardSetPaged[];
  hasMore: boolean;
  total: number;
}

export interface CardSet {
  baseSetSize: number;
  code: string;
  canonicalCode?: string;
  catalogueSetCodes?: string[];
  keyruneCode: string;
  name: string;
  releaseDate: string;
  totalSetSize: number;
  type: SetType;
  marketCap?: number;
  pricing: PricingConfiguration;
  game: GameId;
  slug: string;
  hasSealedProducts: boolean;
}

export type SetType =
  | "alchemy"
  | "archenemy"
  | "arsenal"
  | "box"
  | "commander"
  | "core"
  | "draft_innovation"
  | "duel_deck"
  | "expansion"
  | "from_the_vault"
  | "funny"
  | "masterpiece"
  | "masters"
  | "memorabilia"
  | "minigame"
  | "planechase"
  | "premium_deck"
  | "promo"
  | "spellbook"
  | "starter"
  | "token"
  | "treasure_chest"
  | "vanguard";

export interface CardSetPaged extends CardSet {
  total: number;
}

export interface SealedProductCategoriesResponse {
  items: SealedProductCategory[];
}

export interface SealedProductCategory {
  category: string;
  list: SealedProductResponse[];
}

export interface SealedProductPurchaseUrls {
  cardKingdom: string;
  cardKingdomFoil: string;
  cardmarket: string;
  tcgPlayer: string;
}

export interface SealedProductResponse {
  id: string;
  slug: string;
  name: string;
  cardCount: number;
  category: string;
  subType?: string;
  releaseDate?: string;
  contents: SealedProductContents;
  price?: EmbeddedCardPrice;
  purchaseUrls: SealedProductPurchaseUrls;
  boosterConfigs?: Record<string, BoosterConfigSummary>;
}

export interface BoosterConfigSummary {
  name: string;
  boostersTotalWeight: number;
  variations: BoosterVariationSummary[];
  sheets: Record<string, BoosterSheetSummary>;
}

export interface BoosterVariationSummary {
  weight: number;
  probability: number;
  slots: BoosterSlot[];
}

export interface BoosterSlot {
  sheetName: string;
  count: number;
}

export interface BoosterSheetSummary {
  cardCount: number;
  totalWeight: number;
  foil: boolean;
  fixed?: boolean;
  allowDuplicates?: boolean;
  balanceColors?: boolean;
}

export interface SealedProductSitemapEntry {
  slug: string;
  name: string;
  code: string;
  game: string;
  setName: string;
}

export interface SealedProductSitemapResponse {
  items: SealedProductSitemapEntry[];
}

export interface SealedProductContents {
  card: SealedProductContentCard[];
  deck: SealedProductContentDeck[];
  other: SealedProductContentOther[];
  pack: SealedProductContentPack[];
  sealed: SealedProductContentSealed[];
}

export interface SealedProductContentCard {
  foil: boolean;
  id: string;
  name: string;
  number: string;
  set: string;
}

export interface SealedProductContentDeck {
  name: string;
  set: string;
}

export interface SealedProductContentOther {
  name: string;
}

export interface SealedProductContentPack {
  code: string;
  set: string;
}

export interface SealedProductContentSealed {
  count: number;
  id: string;
  name: string;
  set: string;
  slug?: string;
}

export interface SealedProductFoundIn {
  id: string;
  slug: string;
  name: string;
  category: string;
  count: number;
  pricePerUnit: number | null;
}
